import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import { useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import { LIKE_OUTING, UNLIKE_OUTING } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";

/**
 * Like / unlike toggle for one outing, with the like count beside it.
 *
 * Used both on outing cards (inside the card's `<Link>`) and on the outing
 * page. Because a card wraps this in a link, every branch has to stop the
 * click from bubbling — otherwise liking also navigates to the outing.
 *
 * Anonymous visitors get the count as a plain, non-interactive figure rather
 * than a disabled button: `viewerHasLiked` is false for them server-side, so
 * a heart would always read as "not liked" and clicking it can only fail.
 */
export function LikeButton({
  outingId,
  likeCount,
  viewerHasLiked,
  className = "",
}: {
  outingId: string;
  likeCount: number;
  viewerHasLiked: boolean;
  className?: string;
}) {
  const { personId } = useAuth();
  const { t } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: () =>
      gqlRequest(viewerHasLiked ? UNLIKE_OUTING : LIKE_OUTING, { outingId, personId }),
    // The count lives on the card, so every surface rendering one goes stale:
    // the feed, a profile's outing list, a route's outings, and the outing
    // page itself. Invalidated by key prefix rather than blanket — a bare
    // invalidateQueries() would also refetch the map's viewport queries, which
    // carry no like state and are the expensive ones.
    onSuccess: () => {
      for (const key of [["feed"], ["profile"], ["route"], ["outing", outingId]]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  const base = `inline-flex items-center gap-1.5 text-sm tabular-nums ${className}`;

  if (!personId) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate(`/login?next=${encodeURIComponent(`/outing/${outingId}`)}`);
        }}
        aria-label={t("like.ariaLogin", { count: likeCount })}
        className={`${base} text-muted-foreground transition-colors hover:text-rose-600 dark:hover:text-rose-400`}
      >
        <Heart aria-hidden className="size-4" />
        {likeCount}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle.mutate();
      }}
      disabled={toggle.isPending}
      aria-pressed={viewerHasLiked}
      aria-label={t(viewerHasLiked ? "like.ariaUnlike" : "like.ariaLike", { count: likeCount })}
      className={`${base} transition-colors disabled:opacity-50 ${
        viewerHasLiked ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400"
      }`}
    >
      <Heart aria-hidden className={`size-4 ${viewerHasLiked ? "fill-current" : ""}`} />
      {likeCount}
    </button>
  );
}
